import {
  HOSTED_RUNTIME_PROJECT_CREATE_ROUTE,
  HOSTED_RUNTIME_REQUIRED_ROUTES,
  validateHostedRuntimeContract,
} from "@/services/openanalyst-hosted-runtime"
import {
  HOSTED_PROJECT_ROOT_UPLOAD_MESSAGE,
  isRootShapedHostedUpload,
  shouldUploadHostedProjectFilePath,
} from "@/services/hosted-project-upload-filter"
import type { ProjectStarterTemplateID } from "@/services/project-starters"

type HostedProjectFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

type CreateHostedProjectInput = {
  serverUrl: string
  name: string
  fetch?: HostedProjectFetch
  validateRuntimeContract?: boolean
}

type CreateHostedStarterProjectInput = Omit<CreateHostedProjectInput, "name"> & {
  templateId: ProjectStarterTemplateID
  requestId?: string
  binding?: {
    handle: string
    workspace: string
  }
}

type UploadHostedProjectInput = CreateHostedProjectInput & {
  files: File[]
}

type CreateHostedProjectResult = {
  directory: string
}

type WorkspaceProject = {
  directory?: unknown
  starter?: {
    templateId?: unknown
    starterRequestId?: unknown
  }
}

type UploadHostedProjectResult = CreateHostedProjectResult & {
  uploadedFiles: number
}

const HOSTED_PROJECT_CREATE_MISSING_MESSAGE =
  "Cloud project creation is unavailable because the hosted runtime proxy is missing project routes."
const HOSTED_PROJECT_STARTER_MISSING_MESSAGE =
  "Cloud project starters are unavailable because the hosted runtime proxy is missing project routes."
const HOSTED_PROJECT_UPLOAD_MISSING_MESSAGE =
  "Cloud project upload is unavailable because the hosted runtime proxy is missing project routes."

function runtimeUrl(serverUrl: string, path: string) {
  return `${serverUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`
}

function errorMessage(value: unknown, fallback: string) {
  if (value && typeof value === "object" && "error" in value && value.error && typeof value.error === "object") {
    const message = (value.error as { message?: unknown }).message
    if (typeof message === "string") return message
  }
  if (value && typeof value === "object" && "error" in value && typeof value.error === "string") return value.error
  if (value && typeof value === "object" && "message" in value && typeof value.message === "string") return value.message
  if (value && typeof value === "object" && "detail" in value && typeof value.detail === "string") return value.detail
  return fallback
}

function rawFileRelativePath(file: File) {
  const candidate = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
  return candidate.replace(/\\/g, "/").replace(/^\/+/, "")
}

function safeRelativePath(value: string) {
  const normalized = value.replace(/\\/g, "/").replace(/^\/+/, "")
  if (!normalized || /^[A-Za-z]:/.test(normalized) || /[\x00-\x1f]/.test(normalized)) return null
  const segments = normalized.split("/")
  if (segments.some((segment) => segment === "")) return null
  if (segments.length === 0 || segments.some((segment) => segment === "." || segment === "..")) return null
  return segments.join("/")
}

function uploadRoot(paths: string[]) {
  const roots = new Set(paths.map((path) => path.split("/")[0]).filter((item): item is string => !!item))
  return roots.size === 1 ? [...roots][0] : null
}

function uploadPath(path: string, file: File, root: string | null) {
  if (!root) return path
  if (path === root) return safeRelativePath(file.name) ?? path
  return path.replace(new RegExp(`^${root.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/`), "")
}

function directoryFromResponse(value: unknown, fallback: string) {
  const directory = value && typeof value === "object" && "directory" in value ? value.directory : undefined
  if (typeof directory !== "string" || !directory) throw new Error(fallback)
  return directory
}

function starterRequestId() {
  if (typeof crypto === "object" && "randomUUID" in crypto) return crypto.randomUUID()
  return `starter-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function shouldRecoverStarterResponse(status: number) {
  return status === 408 || status === 500 || status === 502 || status === 503 || status === 504
}

function workspaceProjectForStarterRequest(value: unknown, templateId: ProjectStarterTemplateID, requestId: string) {
  const projects = value && typeof value === "object" && "projects" in value ? value.projects : undefined
  if (!Array.isArray(projects)) return undefined
  return projects.find((item: WorkspaceProject) => {
    if (!item || typeof item !== "object") return false
    return item.starter?.templateId === templateId && item.starter?.starterRequestId === requestId && typeof item.directory === "string"
  }) as WorkspaceProject | undefined
}

function starterProjectCreateBody(input: {
  templateId: ProjectStarterTemplateID
  requestId: string
  binding?: CreateHostedStarterProjectInput["binding"]
}) {
  return JSON.stringify({
    templateId: input.templateId,
    requestId: input.requestId,
    ...(input.binding ? { binding: input.binding } : {}),
  })
}

async function postHostedStarterProject(input: {
  serverUrl: string
  fetch: HostedProjectFetch
  templateId: ProjectStarterTemplateID
  requestId: string
  binding?: CreateHostedStarterProjectInput["binding"]
}) {
  return input.fetch(runtimeUrl(input.serverUrl, "/project/starter"), {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: starterProjectCreateBody(input),
  })
}

async function recoverHostedStarterProject(input: {
  serverUrl: string
  fetch: HostedProjectFetch
  templateId: ProjectStarterTemplateID
  requestId: string
}) {
  for (let attempt = 0; attempt < 180; attempt++) {
    const response = await input.fetch(runtimeUrl(input.serverUrl, "/project/workspace")).catch(() => undefined)
    const data = await response?.json().catch(() => undefined)
    if (response?.ok) {
      const project = workspaceProjectForStarterRequest(data, input.templateId, input.requestId)
      if (typeof project?.directory === "string") return { directory: project.directory }
    }

    await new Promise((resolve) => setTimeout(resolve, attempt < 10 ? 750 : 1_000))
  }
  return undefined
}

async function validateProjectRuntimeContract(input: {
  serverUrl: string
  fetch: HostedProjectFetch
  route: typeof HOSTED_RUNTIME_PROJECT_CREATE_ROUTE | (typeof HOSTED_RUNTIME_REQUIRED_ROUTES)[number]
  validateRuntimeContract?: boolean
}) {
  if (!input.validateRuntimeContract) return
  await validateHostedRuntimeContract({
    serverUrl: input.serverUrl,
    fetch: input.fetch,
    requiredRoutes: [input.route],
  })
}

export async function createHostedProject(input: CreateHostedProjectInput): Promise<CreateHostedProjectResult> {
  const doFetch = input.fetch ?? globalThis.fetch
  await validateProjectRuntimeContract({
    serverUrl: input.serverUrl,
    fetch: doFetch,
    route: HOSTED_RUNTIME_PROJECT_CREATE_ROUTE,
    validateRuntimeContract: input.validateRuntimeContract,
  })
  const response = await doFetch(runtimeUrl(input.serverUrl, "/project/create"), {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ name: input.name }),
  })
  const data = await response.json().catch(() => undefined)
  if (!response.ok) {
    throw new Error(response.status === 404 ? HOSTED_PROJECT_CREATE_MISSING_MESSAGE : errorMessage(data, "Project could not be created"))
  }
  return { directory: directoryFromResponse(data, "Project response did not include a directory") }
}

export async function createHostedStarterProject(input: CreateHostedStarterProjectInput): Promise<CreateHostedProjectResult> {
  const doFetch = input.fetch ?? globalThis.fetch
  await validateProjectRuntimeContract({
    serverUrl: input.serverUrl,
    fetch: doFetch,
    route: "/project/starter",
    validateRuntimeContract: input.validateRuntimeContract,
  })
  const requestId = input.requestId ?? starterRequestId()
  const response = await postHostedStarterProject({
    serverUrl: input.serverUrl,
    fetch: doFetch,
    templateId: input.templateId,
    requestId,
    binding: input.binding,
  }).catch(async (error) => {
    const recovered = await recoverHostedStarterProject({
      serverUrl: input.serverUrl,
      fetch: doFetch,
      templateId: input.templateId,
      requestId,
    })
    if (recovered) return Response.json(recovered)
    throw error
  })
  const data = await response.json().catch(() => undefined)
  if (response.status === 202) {
    const recovered = await recoverHostedStarterProject({
      serverUrl: input.serverUrl,
      fetch: doFetch,
      templateId: input.templateId,
      requestId,
    })
    if (recovered) return recovered
  }
  if (!response.ok) {
    if (shouldRecoverStarterResponse(response.status)) {
      const recovered = await recoverHostedStarterProject({
        serverUrl: input.serverUrl,
        fetch: doFetch,
        templateId: input.templateId,
        requestId,
      })
      if (recovered) return recovered
    }
    throw new Error(response.status === 404 ? HOSTED_PROJECT_STARTER_MISSING_MESSAGE : errorMessage(data, "Starter project could not be created"))
  }
  return { directory: directoryFromResponse(data, "Starter project response did not include a directory") }
}

export async function uploadHostedProject(input: UploadHostedProjectInput): Promise<UploadHostedProjectResult> {
  if (input.files.length === 0) throw new Error("Choose a folder to upload")
  const paths = input.files.map((file) => {
    const path = safeRelativePath(rawFileRelativePath(file))
    if (!path) throw new Error(`Invalid folder upload path for ${file.name || "file"}`)
    return path
  })
  if (isRootShapedHostedUpload(paths)) throw new Error(HOSTED_PROJECT_ROOT_UPLOAD_MESSAGE)
  const form = new FormData()
  const root = uploadRoot(paths)
  form.set("name", input.name)
  const uploadEntries = input.files
    .map((file, index) => ({ file, path: uploadPath(paths[index]!, file, root) }))
    .filter((entry) => shouldUploadHostedProjectFilePath(entry.path))
  uploadEntries.forEach((entry) => {
    form.append("path", entry.path)
    form.append("file", entry.file, entry.path)
  })
  const doFetch = input.fetch ?? globalThis.fetch
  await validateProjectRuntimeContract({
    serverUrl: input.serverUrl,
    fetch: doFetch,
    route: "/project/upload",
    validateRuntimeContract: input.validateRuntimeContract,
  })
  const response = await doFetch(runtimeUrl(input.serverUrl, "/project/upload"), {
    method: "POST",
    body: form,
  })
  const data = await response.json().catch(() => undefined)
  if (!response.ok) {
    throw new Error(response.status === 404 ? HOSTED_PROJECT_UPLOAD_MISSING_MESSAGE : errorMessage(data, "Project folder could not be uploaded"))
  }
  const uploadedFiles =
    data && typeof data === "object" && "uploadedFiles" in data && typeof data.uploadedFiles === "number"
      ? data.uploadedFiles
      : uploadEntries.length
  return {
    directory: directoryFromResponse(data, "Project upload response did not include a directory"),
    uploadedFiles,
  }
}
